import type { ParseKeys, TFunction } from 'i18next';

import { trackerRowWriteErrorCode, type TrackerRowWriteErrorCode } from '@/entities/tracker';

const REFUSAL_REASON_KEYS = {
  record_edit_forbidden: 'trackersBoard.useRecordBoard.refusedRecordEditForbidden',
  tracker_view_only: 'trackersBoard.useRecordBoard.refusedTrackerViewOnly',
  invalid_field_value: 'trackersBoard.useRecordBoard.refusedInvalidFieldValue',
  start_date_after_deadline: 'trackersBoard.useRecordBoard.refusedStartDateAfterDeadline',
  asset_name_taken: 'trackersBoard.useRecordBoard.refusedAssetNameTaken',
  row_not_found: 'trackersBoard.useRecordBoard.refusedRowNotFound',
  tracker_item_archived: 'trackersBoard.useRecordBoard.refusedItemArchived',
} as const satisfies Record<TrackerRowWriteErrorCode, ParseKeys<'trackers'>>;

/**
 * Why a cell write was refused, as a message key, or that the cell went back
 * to its saved value when the server did not say in a way we know.
 * `overrides` lets a board word a reason in its own terms.
 */
export function rowWriteRefusalKey(
  error: unknown,
  overrides: Partial<Record<TrackerRowWriteErrorCode, ParseKeys<'trackers'>>> = {},
): ParseKeys<'trackers'> {
  const code = trackerRowWriteErrorCode(error);
  if (!code) return 'trackersBoard.useRecordBoard.changeUndone';
  return overrides[code] ?? REFUSAL_REASON_KEYS[code];
}

/**
 * The notice for a refused cell write, as `notificationService.showError` arguments: which
 * cell was not saved, and why. Without it the rollback alone reads as the board ignoring
 * the edit.
 */
export function rowWriteRefusalNotice(
  t: TFunction<'trackers'>,
  error: unknown,
  fieldLabel: string | undefined,
  overrides?: Partial<Record<TrackerRowWriteErrorCode, ParseKeys<'trackers'>>>,
): [string, { description: string }] {
  return [
    fieldLabel
      ? t('trackersBoard.useRecordBoard.notSaved', { field: fieldLabel })
      : t('trackersBoard.useRecordBoard.notSavedChange'),
    { description: t(rowWriteRefusalKey(error, overrides)) },
  ];
}
